import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { SignalRService } from './signalr.service';
import { AppUrls } from '../../app-urls';
import { Utilities } from '../../utilities';
import { Errors } from '../../common/errors';
import { ServerLoggerService } from '../../services/server.service';
import { ParticipantOffersUrlBuilderService } from '../../services/participant-offers-url-builder.service';
import { LkAlertAppearedEventHandler } from '../handlers/lk-alert-appeared-event-handler';
import { PageRefreshRequiredEventHandler } from '../handlers/page-refresh-required-event-handler';
import {
  TargetFileFormingResultAppearedEventHandler
} from '../handlers/target-file-forming-result-appeared-event-handler';
import { DialogsService } from '../../services/dialog.service';
import { EventsNames } from '../enums/events-names.enum';

@Injectable({
  providedIn: 'root'
})
export class EventsService {
  private restartOnCloseTimeout = 5000;

  protected checkSignalrConnectionSubscription: Subscription = null;

  private serverDateBehaviorSubject: BehaviorSubject<any> = null;

  constructor(
    private _urlBuilder: ParticipantOffersUrlBuilderService,
    private _signalRService: SignalRService,
    private _dialog: DialogsService,
    private _serverLogger: ServerLoggerService,
    private _targetFileFormingResultAppearedEventHandler: TargetFileFormingResultAppearedEventHandler,
    private _lkAlertAppearedEventHandler: LkAlertAppearedEventHandler,
    private _pageRefreshRequiredEventHandler: PageRefreshRequiredEventHandler
  ) {}

  public async start(serverDateBehaviorSubject: BehaviorSubject<any>): Promise<void> {
    this.serverDateBehaviorSubject = serverDateBehaviorSubject;
    this.initSignalR();
    this.startCheckSignalrConnection();
    await this.connectSignalR();
  }

  public async stop(): Promise<void> {
    this.stopCheckSignalrConnection();
    await this._signalRService.disconnect();
  }

  private initSignalR() {
    this._signalRService.init(this.serverDateBehaviorSubject, this.buildSignalRUrl());
    this.initSignalRCallbacks();
    this.subscribeToEvents();
  }

  private buildSignalRUrl(): string {
    return this._urlBuilder.getAbsoluteTenderPlatformEventsUrl(AppUrls.events.url);
  }

  private initSignalRCallbacks() {
    this._signalRService.setOnCloseCallback(this.signalROnCloseCallback.bind(this));
    this._signalRService.setOnReconnectingCallback(this.signalROnReconnectingCallback.bind(this));
  }

  protected subscribeToEvents() {
    this.subscribeToEvent(
      'TargetFileFormingResultAppeared',
      this._targetFileFormingResultAppearedEventHandler
    );

    this.subscribeToEvent(EventsNames.LkAlertAppeared, this._lkAlertAppearedEventHandler);

    this.subscribeToEvent(EventsNames.PageRefreshRequired, this._pageRefreshRequiredEventHandler);
  }

  protected subscribeToEvent(eventName: string, handler: any): void {
    this._signalRService.setMethodCallback(eventName, (eventData, eventId) => {
      handler.handle.call(handler, eventData, eventId, eventName);
    });
  }

  private async connectSignalR(): Promise<void> {
    if (this._signalRService.isConnectionMaxAttemptCountReached()) {
      this.stopCheckSignalrConnection();
      this._dialog.showInternetProblem();
      return;
    }

    try {
      await this._signalRService.connect();
    } catch (error) {
      if (Errors.isUnauthorized(error)) {
        this.stopCheckSignalrConnection();
        this.logError(error, 'Не удалось подключиться к серверу для получения информации о событиях площадки. Пользователь не авторизован. ');
      } else {
        this.logError(error, `Не удалось подключиться к серверу для получения информации о событиях площадки, используя транспорт: ${this._signalRService.getTransport()}.`);

        if (this._signalRService.switchTransport()) {
          this.initSignalR();
          await this.connectSignalR();
        }
      }
    }
  }

  private signalROnCloseCallback(error?: Error) {
    if (!Utilities.isUndefined(error)) {
      this.logError(error);
    }
  }

  private startCheckSignalrConnection() {
    if (Utilities.isNull(this.checkSignalrConnectionSubscription)) {
      this.checkSignalrConnectionSubscription = interval(this.restartOnCloseTimeout).subscribe(() => {
        if (this._signalRService.isDisconnected()) {
          this.connectSignalR();
        }
      });
    }
  }

  private stopCheckSignalrConnection() {
    if (this.checkSignalrConnectionSubscription !== null) {
      this.checkSignalrConnectionSubscription.unsubscribe();
      this.checkSignalrConnectionSubscription = null;
    }
  }

  private signalROnReconnectingCallback(error?: Error) {
    if (!Utilities.isUndefined(error)) {
      this.logError(error);
    }

    if (Errors.isUnauthorized(error)) {
      this.stopCheckSignalrConnection();
      this._signalRService.disconnect();
    }
  }

  private logError(error: any, message?: string) {
    this._serverLogger.logErrorWithMessage(error, 'Events.SignalR error: ' + message);
  }
}
